import * as React from 'react'
import {Dialog as DialogPrimitive} from 'radix-ui'

import {CloseGlyph} from '@/components/ui/icons'
import {cn} from '@/lib/utils'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger

function DialogPortal(props: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogOverlay({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn('fixed inset-0 z-50 bg-foreground/20 transition-opacity duration-150', className)}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {showCloseButton?: boolean}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // `overflow-x-hidden` + `break-words` keep long tokens inside; `grid-cols-[minmax(0,1fr)]` caps the
          // implicit track, which otherwise grows to a wide grandchild's min-content.
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-md grid-cols-[minmax(0,1fr)] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-x-hidden rounded-xl border border-border bg-popover p-6 text-popover-foreground shadow-[var(--shadow-pop)] outline-none transition-[opacity,transform] duration-150 break-words',
          className
        )}
        {...props}>
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-4 right-4 flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:ring-3 focus-visible:ring-ring/50">
            {/* 24×24: mínimo de WCAG 2.2 SC 2.5.8. El anclaje en 4 deja la cruz en el mismo punto óptico. */}
            <CloseGlyph size={14} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('surface-divider -mx-6 flex flex-col gap-1.5 border-b px-6 pb-4', className)}
      {...props}
    />
  )
}

function DialogFooter({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      // `flex-col-reverse` en móvil: el primario queda arriba, donde cae el pulgar.
      className={cn(
        'surface-divider -mx-6 -mb-2 flex flex-col-reverse gap-2 border-t px-6 pt-4 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-heading text-lg leading-none text-foreground', className)}
      {...props}
    />
  )
}

function DialogDescription({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger}
